// @flow

import React, { type Node } from 'react';
import logStats from 'app/common/lib/logStats';
import { logToServer } from 'app/packages/error-boundary/logger';

type ErrorInfo = {
  componentStack: string,
};

type Props = {
  children: Node,
  renderErrorState?: ({ error: Error, info: ErrorInfo, resetError: () => void }) => Node,
  name: string,
};

type State = {
  error: ?Error,
  info: ?ErrorInfo,
};

const getRootComponentFromComponentStack = (componentStack: string) => {
  /* Example stack information:
     in ComponentThatThrows (created by App)
     in ErrorBoundary (created by App)
     in div (created by App)
     in App
  */
  const lines = componentStack.split(/\s*\n\s*/g).filter(Boolean);
  const [, match = 'Unknown'] = lines[0].match(/in\s([^\s]*)/) || [];
  return match;
};

export default class ErrorBoundary extends React.Component<Props, State> {
  state = {
    error: null,
    info: null,
  };

  componentDidCatch(error: Error, info: ErrorInfo) {
    const isNotInProductionMode = process.env.NODE_ENV !== 'production';

    if (isNotInProductionMode) {
      console.error('Error:', error); // eslint-disable-line no-console
      console.error('Error info:', info); // eslint-disable-line no-console
    }

    try {
      // log the actual error with the component stack
      logToServer({
        errorBoundary: this.props.name,
        message: error.message,
        name: 'React ErrorBoundary Exception',
        stack: error.stack,
      });
      // log a stat counter so we can see which error boundaries are being hit frequently
      logStats.increment('react.error_boundary', 0.1, {
        // component names are mangled in production since we don't manually set displayNames so just logging the rootComponent for dev only
        component: isNotInProductionMode
          ? getRootComponentFromComponentStack(info.componentStack)
          : undefined,
        name: this.props.name,
      });
    } catch (e) {
      // just putting this here to see if there's any edge cases with logging
      logStats.increment('react.error_boundary.error', 1, {
        name: this.props.name,
      });
    }
    this.setState({ error, info });
  }

  resetError = () => {
    this.setState({
      error: null,
      info: null,
    });
  };

  render() {
    const { renderErrorState } = this.props;
    const { error, info } = this.state;

    if (error && info) {
      return renderErrorState
        ? renderErrorState({ error, info, resetError: this.resetError })
        : null;
    }

    return this.props.children;
  }
}
